import React, { useState, useEffect } from "react";
import { Item, StoreDetails, Tool } from "./types";
import Layout from "./components/Layout/Layout";
import Dashboard from "./pages/Dashboard/Dashboard";
import ReceiptPage from "./pages/ReceiptPage/ReceiptPage";
import MarkdownPage from "./pages/MarkdownPage/MarkdownPage";
import SettingsPage from "./pages/SettingsPage/SettingsPage";
import { PrinterProvider } from "./contexts/PrinterContext";
import { loadStoreDetails, saveStoreDetails } from "./utils/storage";
import "./App.css";

function App() {
    // Items state - only need the items, not the setter since management is in ReceiptPage
    const [items] = useState<Item[]>([]);

    // Store details state
    const [storeDetails, setStoreDetails] = useState<StoreDetails>({
        name: "Catelyn Pet Shop",
        address: "Bot Tola Railgate (Sector 4), Uttara, Dhaka",
        phone: "01800-072-072",
        email: "catelynpetshop@gmail.com",
        website: "www.catelynpetshop.com",
    });

    // Active tool state
    const [activeTool, setActiveTool] = useState<string | null>(null);

    // Load store details from local storage on component mount
    useEffect(() => {
        const savedStoreDetails = loadStoreDetails();
        if (savedStoreDetails) {
            setStoreDetails(savedStoreDetails);
        }
    }, []);

    // Define available tools
    const tools: Tool[] = [
        {
            id: "receipt",
            name: "Receipt Generator",
            description: "Create and print receipts for customer purchases",
            icon: (
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    fill="currentColor"
                    viewBox="0 0 16 16"
                >
                    <path d="M5 10.5a.5.5 0 0 1 .5-.5h2a.5.5 0 0 1 0 1h-2a.5.5 0 0 1-.5-.5zm0-2a.5.5 0 0 1 .5-.5h5a.5.5 0 0 1 0 1h-5a.5.5 0 0 1-.5-.5zm0-2a.5.5 0 0 1 .5-.5h5a.5.5 0 0 1 0 1h-5a.5.5 0 0 1-.5-.5zm0-2a.5.5 0 0 1 .5-.5h5a.5.5 0 0 1 0 1h-5a.5.5 0 0 1-.5-.5z" />
                    <path d="M3 0h10a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2v-1h1v1a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1H3a1 1 0 0 0-1 1v1H1V2a2 2 0 0 1 2-2z" />
                    <path d="M1 5v-.5a.5.5 0 0 1 1 0V5h.5a.5.5 0 0 1 0 1h-2a.5.5 0 0 1 0-1H1zm0 3v-.5a.5.5 0 0 1 1 0V8h.5a.5.5 0 0 1 0 1h-2a.5.5 0 0 1 0-1H1zm0 3v-.5a.5.5 0 0 1 1 0v.5h.5a.5.5 0 0 1 0 1h-2a.5.5 0 0 1 0-1H1z" />
                </svg>
            ),
            path: "/receipt",
            component: ReceiptPage,
            isPopular: true,
        },
        {
            id: "markdown",
            name: "Markdown Writer",
            description: "Write and print markdown documents",
            icon: (
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                >
                    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zm-1 2l5 5h-5V4zM6 20V4h5v7h7v9H6z" />
                    <path d="M9.5 12.5h5v1h-5zM9.5 15h5v1h-5zM9.5 17.5h3v1h-3z" />
                </svg>
            ),
            path: "/markdown",
            component: MarkdownPage,
            isNew: true,
        },
        {
            id: "settings",
            name: "Settings",
            description: "Manage store settings and product data",
            icon: (
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                >
                    <path d="M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58a.49.49 0 00.12-.61l-1.92-3.32a.488.488 0 00-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54a.484.484 0 00-.48-.42h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.09.63-.09.94 0 .31.03.64.07.94l-2.03 1.58a.49.49 0 00-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z" />
                </svg>
            ),
            path: "/settings",
            component: SettingsPage,
        },
    ];

    // Store details are saved directly in the state setter
    const updateStoreDetails = (newStoreDetails: StoreDetails) => {
        setStoreDetails(newStoreDetails);
        saveStoreDetails(newStoreDetails);
    };

    // Find the active tool component
    const renderActiveTool = () => {
        const tool = tools.find((t) => t.id === activeTool);
        if (!tool) return null;

        switch (tool.id) {
            case "receipt":
                return (
                    <ReceiptPage
                        initialItems={items}
                        storeDetails={storeDetails}
                    />
                );
            case "markdown":
                return <MarkdownPage />;
            case "settings":
                return (
                    <SettingsPage
                        initialStoreDetails={storeDetails}
                        onStoreDetailsChange={updateStoreDetails}
                    />
                );
            default:
                return (
                    <div className="tool-not-found">
                        <h2>Tool Not Found</h2>
                        <p>The requested tool could not be found.</p>
                        <button onClick={() => handleToolSelect(null)}>
                            Return to Dashboard
                        </button>
                    </div>
                );
        }
    };

    // Tool selection handler
    const handleToolSelect = (toolId: string | null) => {
        setActiveTool(toolId);
    };

    return (
        <PrinterProvider>
            <div className="App">
                <Layout
                    activeTool={activeTool}
                    tools={tools}
                    onNavigate={handleToolSelect}
                >
                    {activeTool === null ? (
                        <Dashboard
                            tools={tools}
                            onSelectTool={handleToolSelect}
                        />
                    ) : (
                        renderActiveTool()
                    )}
                </Layout>
            </div>
        </PrinterProvider>
    );
}

export default App;
